/**
 * ClaireTour — guided onboarding narrated by Claire (react-joyride).
 * Part 1: anchored hotspots over the reorganized Sidebar and the
 * Intelligence Dashboard. Niche-aware tips (Construction vs Retail) and a
 * one-time 10-credit bonus on completion (POST /api/user/tour-complete).
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Joyride, EVENTS, STATUS } from "react-joyride";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, X, HardHat, Store, BookOpen, Gift } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NICHE_KEY = "alp.tour.niche";

const STEP_DEFS = [
  {
    target: '[data-testid="sidebar-item-agency-dashboard"]',
    doc: "/dashboard",
    pt: {
      title: "Seu Dashboard de Inteligência",
      body: "Eu moro aqui! Todos os dias resumo o que encontrei: leads quentes, licitações novas e o que precisa da sua atenção.",
      tipC: "Para construção, fico de olho em editais SAM.gov e USASpending todas as manhãs.",
      tipR: "Para varejo, monitoro intenção de compra B2C e menções sociais em tempo real.",
    },
    en: {
      title: "Your Intelligence Dashboard",
      body: "I live here! Every day I summarize what I found: hot leads, new bids and what needs your attention.",
      tipC: "For construction, I watch SAM.gov and USASpending bids every morning.",
      tipR: "For retail, I monitor B2C purchase intent and social mentions in real time.",
    },
  },
  {
    target: '[data-testid="sidebar-item-sourcing"]',
    doc: "/prospecting",
    pt: {
      title: "Lead Sourcing — onde tudo começa",
      body: "Aqui ficam minhas ferramentas de captura: Prospecting Engine, módulo GOV, radar B2C e scrapers de redes sociais.",
      tipC: "Comece pelo Procurement Scanner: mostro contratos públicos com dinheiro na mesa, por estado.",
      tipR: "Comece pelo B2C Intent: encontro pessoas pedindo indicação do seu serviço agora.",
    },
    en: {
      title: "Lead Sourcing — where it all starts",
      body: "My capture tools live here: Prospecting Engine, GOV module, B2C radar and social scrapers.",
      tipC: "Start with the Procurement Scanner: public contracts with money on the table, by state.",
      tipR: "Start with B2C Intent: I find people asking for your service right now.",
    },
  },
  {
    target: '[data-testid="sidebar-item-pipeline"]',
    doc: "/kanban",
    pt: {
      title: "Sales Pipeline — seu funil visual",
      body: "CRM Kanban para arrastar leads entre fases e o Mapa de Leads para planejar visitas de campo com rotas otimizadas.",
      tipC: "No CRM Gov eu calculo o Victory Index (0-10) de cada edital para você priorizar.",
      tipR: "Use o Mapa de Leads para agrupar visitas por bairro e economizar combustível.",
    },
    en: {
      title: "Sales Pipeline — your visual funnel",
      body: "Kanban CRM to drag leads between stages and the Leads Map to plan field visits with optimized routes.",
      tipC: "In the Gov CRM I compute a Victory Index (0-10) for every bid so you can prioritize.",
      tipR: "Use the Leads Map to cluster visits by neighborhood and save fuel.",
    },
  },
  {
    target: '[data-testid="sidebar-item-intelligence"]',
    doc: "/lead-gap",
    pt: {
      title: "AI Intelligence — meu cérebro",
      body: "Ferramentas onde eu penso por você: análise de gaps, configurador SaaS e automações inteligentes.",
      tipC: "O Lead Gap mostra materiais que os concorrentes vendem para o governo e você ainda não.",
      tipR: "Descubro nichos com alta demanda e pouca concorrência na sua região.",
    },
    en: {
      title: "AI Intelligence — my brain",
      body: "Tools where I think for you: gap analysis, SaaS configurator and smart automations.",
      tipC: "Lead Gap shows materials competitors sell to the government that you don't yet.",
      tipR: "I uncover niches with high demand and low competition in your area.",
    },
  },
  {
    target: '[data-testid="credits-chip"]',
    doc: "/billing/history",
    pt: {
      title: "Seus créditos de IA",
      body: "Cada ação minha (capturar lead, redigir e-mail, calcular score) usa créditos. Acompanhe o saldo aqui e recarregue quando precisar.",
      tipC: "Dica: o Auto Pilot usa créditos para triar editais sozinho enquanto você dorme.",
      tipR: "Dica: rascunhos de e-mail custam só 1 crédito e economizam 10 minutos cada.",
    },
    en: {
      title: "Your AI credits",
      body: "Every action I take (capture lead, draft email, compute score) uses credits. Track your balance here and top up when needed.",
      tipC: "Tip: Auto Pilot spends credits triaging bids on its own while you sleep.",
      tipR: "Tip: email drafts cost just 1 credit and save 10 minutes each.",
    },
  },
  {
    target: '[data-testid="claire-daily-brief"]',
    doc: "/dashboard",
    pt: {
      title: "Meu Resumo Diário",
      body: "Todo dia escrevo um briefing do que aconteceu: matches dourados, prazos próximos e leads esquecidos no CRM.",
      tipC: "Aviso quando um edital compatível com seu catálogo fecha em 48h.",
      tipR: "Aviso quando um lead quente ficou 3 dias sem follow-up.",
    },
    en: {
      title: "My Daily Brief",
      body: "Every day I write a briefing: golden matches, closing deadlines and forgotten CRM leads.",
      tipC: "I warn you when a bid matching your catalog closes within 48h.",
      tipR: "I warn you when a hot lead sat 3 days without follow-up.",
    },
  },
  {
    target: '[data-testid="intelligence-feed"]',
    doc: "/dashboard",
    pt: {
      title: "Intelligence Feed — ao vivo",
      body: "Tudo que eu descubro aparece aqui em tempo real. Clique em qualquer item e eu te levo direto ao lead, já destacado.",
      tipC: "Novos editais compatíveis chegam aqui com link direto para o CRM Gov.",
      tipR: "Respostas de WhatsApp e e-mails encontrados aparecem aqui na hora.",
    },
    en: {
      title: "Intelligence Feed — live",
      body: "Everything I discover shows up here in real time. Click any item and I take you straight to the highlighted lead.",
      tipC: "New matching bids arrive here with a direct link to the Gov CRM.",
      tipR: "WhatsApp replies and discovered emails show up here instantly.",
    },
  },
  {
    target: '[data-testid="manage-sources-btn"]',
    doc: "/settings/sources",
    pt: {
      title: "Gerenciar Fontes de Dados",
      body: "Última missão! Aqui você liga e desliga minhas fontes: Google Maps, SAM.gov, Yelp, Instagram e muito mais.",
      tipC: "Ative SAM.gov + Sunbiz para cobrir federal e Flórida de uma vez.",
      tipR: "Ative Google Maps + Yelp para mapear todo o comércio da sua cidade.",
    },
    en: {
      title: "Manage Data Sources",
      body: "Last mission! Turn my sources on and off here: Google Maps, SAM.gov, Yelp, Instagram and more.",
      tipC: "Enable SAM.gov + Sunbiz to cover federal and Florida at once.",
      tipR: "Enable Google Maps + Yelp to map every business in your city.",
    },
  },
];

function ClaireTooltip({ step, index, size, primaryProps, backProps, skipProps, isLastStep, isPt, tooltipProps }) {
  const meta = step.data || {};
  return (
    <div {...(tooltipProps || {})} className="w-[340px] max-w-[calc(100vw-32px)] rounded-2xl overflow-hidden shadow-2xl bg-white" data-testid="claire-tour-tooltip">
      <div className="px-4 py-3 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold">Claire · {isPt ? "sua assistente" : "your assistant"}</div>
          <div className="text-[13.5px] font-extrabold leading-tight">{meta.title}</div>
        </div>
        <span className="text-[10px] font-bold text-white/70 tabular-nums shrink-0">{index + 1}/{size}</span>
        <button type="button" {...skipProps} data-testid="claire-tour-skip" className="shrink-0 p-1 rounded hover:bg-white/15">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-4 space-y-2.5">
        <p className="text-[12.5px] leading-relaxed text-[#374151]">{meta.body}</p>
        {meta.tip && (
          <p className="text-[11.5px] leading-snug rounded-lg bg-[#8B5CF6]/8 border border-[#8B5CF6]/20 px-2.5 py-2 text-[#6D28D9]">
            💡 {meta.tip}
          </p>
        )}
        <div className="flex items-center gap-2 pt-1">
          <a href={meta.doc || "#"} target="_blank" rel="noopener noreferrer"
            data-testid="claire-tour-doc-link"
            className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#1B3A8A] hover:underline mr-auto">
            <BookOpen className="w-3 h-3" /> {isPt ? "Documentação" : "Docs"}
          </a>
          {index > 0 && (
            <button type="button" {...backProps} data-testid="claire-tour-back"
              className="h-9 px-3 rounded-lg border border-[#E5E7EB] text-[11.5px] font-semibold text-[#6B7280]">
              {isPt ? "Voltar" : "Back"}
            </button>
          )}
          <button type="button" {...primaryProps} data-testid="claire-tour-next"
            className="h-9 px-4 rounded-lg bg-[#1B3A8A] text-white text-[11.5px] font-extrabold inline-flex items-center gap-1.5">
            {isLastStep ? (<><Gift className="w-3.5 h-3.5" /> {isPt ? "Concluir (+10 créditos)" : "Finish (+10 credits)"}</>)
              : (isPt ? "Próximo" : "Next")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClaireTour({ open, onClose }) {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const navigate = useNavigate();
  const location = useLocation();
  const [niche, setNiche] = useState(() => {
    try { return window.localStorage.getItem(NICHE_KEY) || ""; } catch (_e) { return ""; }
  });
  const [run, setRun] = useState(false);

  // Tour anchors live on /dashboard — move there when the tour opens.
  useEffect(() => {
    if (!open) { setRun(false); return; }
    if (location.pathname !== "/dashboard") navigate("/dashboard");
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (open && niche && location.pathname === "/dashboard") {
      const t = setTimeout(() => setRun(true), 900);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, niche, location.pathname]);

  const steps = useMemo(() => STEP_DEFS.map((s) => {
    const tx = isPt ? s.pt : s.en;
    return {
      target: s.target,
      content: tx.body,
      skipBeacon: true,
      placement: "auto",
      data: {
        title: tx.title, body: tx.body, doc: s.doc,
        tip: niche === "construction" ? tx.tipC : niche === "retail" ? tx.tipR : "",
      },
    };
  }), [isPt, niche]);

  const complete = async (skipped) => {
    setRun(false);
    onClose?.();
    if (skipped) return;
    try {
      const { data } = await axios.post(`${API}/user/tour-complete`, {});
      if (data.bonus_awarded) {
        toast.success(isPt
          ? "🎁 Missões concluídas! +10 créditos adicionados à sua conta."
          : "🎁 Missions complete! +10 credits added to your account.",
          { duration: 8000 });
      } else {
        toast.success(isPt ? "Tour concluído!" : "Tour complete!");
      }
    } catch (_e) { /* non-blocking */ }
  };

  const handleEvent = (data) => {
    if (data.type === EVENTS.TOUR_END) {
      complete(data.status === STATUS.SKIPPED);
    }
  };

  if (!open) return null;

  // Phase 1 — Claire asks for the user's niche before starting the ride.
  if (!niche) {
    return (
      <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="claire-tour-niche-modal">
        <div className="w-full max-w-md rounded-2xl overflow-hidden bg-white shadow-2xl">
          <div className="px-5 py-4 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[15px] font-extrabold">{isPt ? "Oi! Eu sou a Claire 👋" : "Hi! I'm Claire 👋"}</div>
              <div className="text-[11.5px] text-white/75">
                {isPt ? "Sua assistente de inteligência. Vou te guiar em 2 minutos." : "Your intelligence assistant. I'll guide you in 2 minutes."}
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <p className="text-[12.5px] text-[#374151]">
              {isPt
                ? "Antes de começar: qual é o seu foco? Assim personalizo as dicas do tour para o seu negócio."
                : "Before we start: what's your focus? I'll tailor the tour tips to your business."}
            </p>
            <button type="button" data-testid="claire-tour-niche-construction"
              onClick={() => { setNiche("construction"); try { localStorage.setItem(NICHE_KEY, "construction"); } catch (_e) {} }}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-[#E5E7EB] hover:border-[#C9A24E] transition-colors text-left">
              <span className="w-10 h-10 rounded-lg bg-[#C9A24E]/15 text-[#92660C] flex items-center justify-center"><HardHat className="w-5 h-5" /></span>
              <span>
                <span className="block text-[13px] font-extrabold text-[#111827]">{isPt ? "Construção & Governo" : "Construction & Gov"}</span>
                <span className="block text-[11px] text-[#6B7280]">{isPt ? "Licitações, empreiteiras, materiais, SAM.gov" : "Bids, contractors, materials, SAM.gov"}</span>
              </span>
            </button>
            <button type="button" data-testid="claire-tour-niche-retail"
              onClick={() => { setNiche("retail"); try { localStorage.setItem(NICHE_KEY, "retail"); } catch (_e) {} }}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-[#E5E7EB] hover:border-[#3B82F6] transition-colors text-left">
              <span className="w-10 h-10 rounded-lg bg-[#3B82F6]/12 text-[#1B3A8A] flex items-center justify-center"><Store className="w-5 h-5" /></span>
              <span>
                <span className="block text-[13px] font-extrabold text-[#111827]">{isPt ? "Varejo & Serviços Locais" : "Retail & Local Services"}</span>
                <span className="block text-[11px] text-[#6B7280]">{isPt ? "B2C, comércio local, redes sociais, Google Maps" : "B2C, local business, social, Google Maps"}</span>
              </span>
            </button>
            <div className="flex items-center justify-between pt-1">
              <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#92660C]">
                <Gift className="w-3.5 h-3.5" /> {isPt ? "Complete o tour e ganhe +10 créditos" : "Finish the tour and earn +10 credits"}
              </span>
              <button type="button" data-testid="claire-tour-niche-skip"
                onClick={() => complete(true)}
                className="text-[11px] text-[#9CA3AF] hover:text-[#6B7280] underline">
                {isPt ? "Pular tour" : "Skip tour"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      tooltipComponent={(props) => <ClaireTooltip {...props} isPt={isPt} />}
      options={{
        zIndex: 10000,
        overlayColor: "rgba(10, 18, 51, 0.55)",
        scrollOffset: 120,
        skipBeacon: true,
        overlayClickAction: false,
        primaryColor: "#8B5CF6",
      }}
      locale={{ back: "Voltar", next: "Próximo", last: "Concluir", skip: "Pular" }}
    />
  );
}
